// Shared building blocks of the Restaurant OS screens (System.dc.html):
// buttons 60 hero / 52 standard / 44 minimum, chips, the live light, states.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { C, FONT_TIGHT } from '../theme/tokens';
import { STATUS } from '@/lib/diningEvents';
import { nowClock } from '../lib/format';
import { IconAlert, IconClose, IconRefresh, IconSpinner, IconWifiOff } from './icons';

// ------------------------------------------------------------------ time

export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// the header clock: the restaurant's time (lib/format setDiningZone), not the device's
export function Clock({ size = 20, color, weight = 700 }) {
  const now = useNow(15000);
  return (
    <span className="c29-tight" data-testid="dining-clock" style={{ fontSize: size, fontWeight: weight, color, letterSpacing: size > 28 ? '-0.5px' : 0, whiteSpace: 'nowrap' }}>
      {nowClock(new Date(now))}
    </span>
  );
}

// ------------------------------------------------------------------ brand

export function Logo29({ size = 36, radius }) {
  return (
    <div aria-hidden="true" className="c29-tight" style={{
      width: size, height: size, borderRadius: radius ?? Math.round(size * 0.28), background: C.orange, color: C.orangeDeep,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: Math.round(size * 0.44),
      letterSpacing: '-0.5px', flex: 'none',
    }}>29</div>
  );
}

// ------------------------------------------------------------------ buttons

const BTN = {
  hero: { height: 60, radius: 16, bg: C.orange, fg: C.orangeDeep, size: 17, weight: 800, border: 'none' },
  orange: { height: 52, radius: 14, bg: C.orange, fg: C.orangeDeep, size: 15, weight: 800, border: 'none' },
  ink: { height: 52, radius: 14, bg: C.ink, fg: '#FFFFFF', size: 15, weight: 700, border: 'none' },
  white: { height: 52, radius: 14, bg: '#FFFFFF', fg: C.ink, size: 15, weight: 700, border: `1px solid ${C.border}` },
  danger: { height: 52, radius: 14, bg: C.redSoft, fg: C.redInk, size: 15, weight: 700, border: 'none' },
  dangerOutline: { height: 56, radius: 14, bg: '#FFFFFF', fg: C.redInk, size: 16, weight: 800, border: '1.5px solid #FCA5A5' },
  green: { height: 56, radius: 14, bg: C.greenHi, fg: '#052E14', size: 16, weight: 700, border: 'none' },
  greenSolid: { height: 56, radius: 14, bg: C.green, fg: '#FFFFFF', size: 16, weight: 800, border: 'none' },
  locked: { height: 52, radius: 14, bg: 'transparent', fg: C.faint, size: 15, weight: 700, border: '1px dashed #D6D3D1' },
  dark: { height: 52, radius: 12, bg: C.panel2, fg: C.text, size: 14, weight: 600, border: `1px solid ${C.line2}` },
  darkLocked: { height: 52, radius: 12, bg: 'transparent', fg: C.textOff, size: 14, weight: 600, border: `1px dashed ${C.line2}` },
  amber: { height: 48, radius: 12, bg: C.amber, fg: '#1F1300', size: 15, weight: 800, border: 'none' },
};

export function Btn({ kind = 'white', height, full, children, style, busy, disabled, icon, iconRight, ...rest }) {
  const k = BTN[kind] || BTN.white;
  return (
    <button type="button" className="c29-press" disabled={disabled || busy} aria-busy={busy || undefined} {...rest}
      style={{
        height: height || k.height, minHeight: 44, borderRadius: k.radius, background: k.bg, color: k.fg,
        border: k.border, fontSize: k.size, fontWeight: k.weight, padding: '0 18px',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: full ? '100%' : undefined,
        opacity: disabled && !busy ? 0.55 : 1, whiteSpace: 'nowrap', ...style,
      }}>
      {busy ? <IconSpinner size={18} /> : icon}
      {children}
      {iconRight}
    </button>
  );
}

export function IconBtn({ label, dark, size = 44, children, style, ...rest }) {
  return (
    <button type="button" aria-label={label} title={label} className="c29-press" {...rest}
      style={{
        width: size, height: size, borderRadius: 12, flex: 'none',
        border: `1px solid ${dark ? C.line2 : C.border}`, background: dark ? 'transparent' : '#FFFFFF',
        color: dark ? C.textMid : C.ink3, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, ...style,
      }}>
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ chips

export function Chip({ bg, fg, dot, children, height = 24, style }) {
  return (
    <span style={{
      height, padding: '0 9px', borderRadius: height / 2, background: bg, color: fg, fontSize: 12, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: 'none', ...style,
    }}>
      {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot }} />}
      {children}
    </span>
  );
}

const TAG = { NEW: '#16A34A', ADDED: '#2563EB', VOIDED: '#DC2626', CHANGED: '#7C3AED', RUSH: '#1C1917' };
export function Tag({ children, color }) {
  const label = String(children);
  return (
    <span style={{
      height: 22, padding: '0 8px', borderRadius: 5, background: color || TAG[label] || C.ink3,
      color: label === 'RUSH' ? '#FDBA74' : '#FFFFFF', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.05em',
      display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none',
    }}>{children}</span>
  );
}

// ------------------------------------------------------------------ the live light

const LIVE = {
  [STATUS.LIVE]: { label: 'Live', color: C.greenText, dot: C.greenHi },
  [STATUS.POLLING]: { label: 'Reconnecting', color: C.amberHi, dot: C.amber },
  [STATUS.CONNECTING]: { label: 'Connecting', color: C.textMid, dot: C.textLo },
  [STATUS.IDLE]: { label: 'Connecting', color: C.textMid, dot: C.textLo },
  [STATUS.SIGNED_OUT]: { label: 'Signed out', color: '#FCA5A5', dot: C.redHi },
  [STATUS.STOPPED]: { label: 'Offline', color: '#FCA5A5', dot: C.redHi },
};

export function LiveIndicator({ status, light, size = 13 }) {
  const s = LIVE[status] || LIVE[STATUS.IDLE];
  const color = light ? (status === STATUS.LIVE ? C.greenInk : status === STATUS.POLLING ? '#B45309' : C.muted) : s.color;
  return (
    <span role="status" aria-live="polite" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: size, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
      <span style={{ width: size > 14 ? 10 : 8, height: size > 14 ? 10 : 8, borderRadius: '50%', background: s.dot, boxShadow: status === STATUS.LIVE ? `0 0 0 ${size > 14 ? 5 : 4}px rgba(34,197,94,.18)` : 'none' }} />
      {s.label}
    </span>
  );
}

// The offline / reconnecting banner (every screen): the stream is down, the
// screen catches up by polling and says so.
export function StreamBanner({ status, light, onRetry }) {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  const show = !online || status === STATUS.POLLING || status === STATUS.STOPPED;
  if (!show) return null;
  const text = !online
    ? 'This device is offline. Changes cannot be sent until it is back online; the screen updates when it reconnects.'
    : 'Live updates paused - reconnecting. The screen is catching up every few seconds.';
  return (
    <div role="status" className="c29-fade" style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', fontSize: 13.5, fontWeight: 600,
      background: light ? '#FEF3C7' : 'rgba(245,158,11,.14)', color: light ? '#92400E' : '#FBBF24',
      borderBottom: `1px solid ${light ? '#FDE68A' : 'rgba(245,158,11,.25)'}`, flex: 'none',
    }}>
      {online ? <IconSpinner size={16} /> : <IconWifiOff size={18} />}
      <span style={{ flex: 1 }}>{text}</span>
      {onRetry && online && (
        <button type="button" onClick={onRetry} style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 800, fontSize: 13.5, minHeight: 32 }}>
          Retry now
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ states

export function Loading({ light, label = 'Loading…', style }) {
  return (
    <div role="status" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32, color: light ? C.muted : C.textLo, fontSize: 14, fontWeight: 600, ...style }}>
      <IconSpinner size={28} color={light ? C.orange : C.orange} />
      {label}
    </div>
  );
}

export function Skeleton({ height = 80, style }) {
  return <div className="c29-skel" style={{ height, ...style }} aria-hidden="true" />;
}

export function Empty({ light, icon, title, body, action, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 10, padding: 32, ...style }}>
      {icon && <div style={{ width: 64, height: 64, borderRadius: 20, background: light ? C.sand : C.panel2, color: light ? C.muted : C.textLo, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>}
      <div style={{ fontSize: 18, fontWeight: 800, color: light ? C.ink : C.textHi }}>{title}</div>
      {body && <div style={{ fontSize: 14, lineHeight: 1.5, color: light ? C.muted : C.textLo, maxWidth: 420 }}>{body}</div>}
      {action}
    </div>
  );
}

export function ErrorNote({ error, light, onRetry, compact, style }) {
  if (!error) return null;
  const text = typeof error === 'string' ? error : error.message || 'Something went wrong.';
  return (
    <div role="alert" style={{
      display: 'flex', alignItems: compact ? 'center' : 'flex-start', gap: 10, padding: compact ? '8px 12px' : '12px 14px', borderRadius: 12,
      background: light ? '#FEF2F2' : 'rgba(239,68,68,.12)', color: light ? C.redInk : '#FCA5A5',
      border: `1px solid ${light ? '#FECACA' : 'rgba(239,68,68,.3)'}`, fontSize: 14, fontWeight: 600, lineHeight: 1.45, ...style,
    }}>
      <span style={{ flex: 'none', marginTop: compact ? 0 : 1 }}><IconAlert size={18} /></span>
      <span style={{ flex: 1 }}>{text}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32, padding: 0 }}>
          <IconRefresh size={16} />Retry
        </button>
      )}
    </div>
  );
}

export function FullError({ error, light, onRetry, title = 'Could not load this screen' }) {
  return (
    <Empty light={light} icon={<IconAlert size={28} />} title={title}
      body={typeof error === 'string' ? error : error?.message}
      action={onRetry && <Btn kind={light ? 'ink' : 'dark'} onClick={onRetry} icon={<IconRefresh size={18} />} style={{ marginTop: 8 }}>Try again</Btn>} />
  );
}

// ------------------------------------------------------------------ modal / sheet

export function Modal({ open, onClose, label, width = 580, children, top, dark }) {
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    document.addEventListener('keydown', esc);
    const first = box.current?.querySelector('button, input, select, textarea');
    if (first) setTimeout(() => first.focus(), 30);
    return () => document.removeEventListener('keydown', esc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="c29-fade" onPointerDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: dark ? 'rgba(0,0,0,.6)' : 'rgba(28,25,23,.45)', display: 'flex', justifyContent: 'center', alignItems: top ? 'flex-start' : 'center', padding: top ? '34px 16px 16px' : 16 }}>
      <div ref={box} role="dialog" aria-modal="true" aria-label={label} className="c29-sheet"
        style={{ width, maxWidth: '100%', maxHeight: 'calc(100vh - 48px)', background: dark ? C.panel : '#FFFFFF', color: dark ? C.text : C.ink, borderRadius: 24, boxShadow: '0 30px 80px rgba(28,25,23,.35)', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: dark ? `1px solid ${C.line2}` : 'none' }}>
        {children}
      </div>
    </div>
  );
}

export function ModalHead({ title, sub, onClose, dark, children }) {
  return (
    <div style={{ padding: '18px 24px 14px', display: 'flex', alignItems: 'flex-start', gap: 16, borderBottom: `1px solid ${dark ? C.line : C.hair}` }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: dark ? C.textHi : C.ink }}>{title}</h2>
        {sub && <span style={{ fontSize: 14, color: dark ? C.textLo : C.muted }}>{sub}</span>}
      </div>
      {children}
      {onClose && <IconBtn label="Close" dark={dark} onClick={onClose}><IconClose size={18} /></IconBtn>}
    </div>
  );
}

export function ModalFoot({ children, dark }) {
  return (
    <div style={{ padding: '14px 24px 20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, borderTop: `1px solid ${dark ? C.line : C.hair}`, background: dark ? C.bar : C.cream }}>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ toasts

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((t) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((list) => [...list.slice(-1), { id, tone: 'info', ...t }]);
    setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), t.ms || 3000);
  }, []);
  const api = { push, error: (text) => push({ tone: 'error', text, ms: 6000 }), ok: (text) => push({ tone: 'ok', text }), info: (text) => push({ tone: 'info', text }) };
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div aria-live="polite" style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 55, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', pointerEvents: 'none', width: 'min(560px, calc(100vw - 32px))' }}>
        {toasts.map((t) => (
          <div key={t.id} className="c29-in" role={t.tone === 'error' ? 'alert' : 'status'} style={{
            pointerEvents: 'auto', width: '100%', borderRadius: 16, padding: '12px 16px', fontSize: 14.5, fontWeight: 700, lineHeight: 1.4,
            background: t.tone === 'error' ? '#7F1D1D' : C.ink, color: '#FFFFFF', boxShadow: '0 18px 40px rgba(0,0,0,.35)',
            display: 'flex', alignItems: 'center', gap: 10, border: '1px solid rgba(255,255,255,.08)',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', background: t.tone === 'error' ? '#FCA5A5' : t.tone === 'ok' ? C.greenHi : C.orange }} />
            <span style={{ flex: 1 }}>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx) || { push: () => {}, error: () => {}, ok: () => {}, info: () => {} };
}

// ------------------------------------------------------------------ inputs

export function Stepper({ value, onDec, onInc, min = 1, max = 999, light = true, busy, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: '#FFFFFF', borderRadius: 12, border: '1px solid #F3D9C3', flex: 'none' }}>
      <button type="button" aria-label={`One fewer ${label || ''}`.trim()} onClick={onDec} disabled={busy || value <= min - 1}
        style={{ width: 44, height: 44, border: 'none', background: 'transparent', color: C.ink, fontSize: 20, fontWeight: 600 }}>−</button>
      <span style={{ minWidth: 20, textAlign: 'center', fontSize: 16, fontWeight: 800 }}>{value}</span>
      <button type="button" aria-label={`One more ${label || ''}`.trim()} onClick={onInc} disabled={busy || value >= max}
        style={{ width: 44, height: 44, border: 'none', background: 'transparent', color: C.ink, fontSize: 20, fontWeight: 600 }}>+</button>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 14, fontWeight: 700 }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 12.5, color: C.muted }}>{hint}</span>}
    </label>
  );
}

export const inputStyle = {
  height: 48, borderRadius: 12, border: `1px solid ${C.border}`, padding: '0 12px', fontSize: 15, color: C.ink, background: '#FAF8F5', width: '100%',
};

export function Segmented({ options, value, onChange, dark, height = 44, style }) {
  return (
    <div role="tablist" style={{ display: 'flex', gap: 4, padding: 4, background: dark ? C.panel2 : C.sand, borderRadius: 14, ...style }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={String(o.value)} type="button" role="tab" aria-selected={on} onClick={() => onChange(o.value)} disabled={o.disabled}
            style={{
              flex: 1, height, padding: '0 14px', border: 'none', borderRadius: 10, whiteSpace: 'nowrap',
              background: on ? (dark ? C.line2 : '#FFFFFF') : 'transparent', color: on ? (dark ? C.textHi : C.ink) : (dark ? C.textMid : C.ink3),
              fontSize: 14.5, fontWeight: on ? 700 : 600, boxShadow: on && !dark ? '0 1px 3px rgba(28,25,23,.15)' : 'none',
              opacity: o.disabled ? 0.45 : 1,
            }}>{o.label}</button>
        );
      })}
    </div>
  );
}

export function Avatar({ text, ring, size = 40, dark = true }) {
  return (
    <div aria-hidden="true" style={{
      width: size, height: size, borderRadius: '50%', background: dark ? C.line2 : C.ink, color: '#F8FAFC', flex: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.35), fontWeight: 700,
      border: ring ? `2px solid ${ring}` : 'none',
    }}>{text}</div>
  );
}

export const tight = { fontFamily: FONT_TIGHT };
